import { Decorator } from 'final-form';

import { geocode } from 'api/google';
import { AddressType, GeocoderResult, PlaceAutocompleteResult } from 'api/google/maps/types';
import { showErrorSnackbar } from 'hooks/useSnackbar';

const getAddressComponent = (geocodingRes: GeocoderResult, types: AddressType[]) => {
  return geocodingRes?.address_components.find((component) =>
    component.types.some((type) => types.includes(type as AddressType)),
  );
};

export const getStateFromGeocoding = (geocodingRes: GeocoderResult) =>
  getAddressComponent(geocodingRes, [AddressType.administrative_area_level_1])?.long_name;

type TFormValues = {
  country: string;
  city: string;
  state: string;
  address: string;
  zip: string;
  geocode: GeocoderResult;
  place: PlaceAutocompleteResult;
};

export const getAddressComponents = (geocodingRes: GeocoderResult) => {
  const geocodeAddress = geocodingRes.formatted_address ?? '';
  const geocodeCountry = getAddressComponent(geocodingRes, [AddressType.country])?.short_name;
  const geocodeCity = getAddressComponent(geocodingRes, [AddressType.locality])?.long_name;
  const geocodeState = getAddressComponent(geocodingRes, [AddressType.administrative_area_level_1])?.long_name;
  const geocodePostalCode = getAddressComponent(geocodingRes, [AddressType.postal_code])?.long_name;

  return {
    address: geocodeAddress,
    country: geocodeCountry,
    city: geocodeCity,
    geocodeState,
    zipCode: geocodePostalCode,
  };
};

export const geocodingDecorator: Decorator<TFormValues, { test: string }> = (form) => {
  let prevPlaceId: string | null = null;

  const unsubscribe = form.subscribe(
    async ({ values }) => {
      if (!('place' in values)) {
        prevPlaceId = null;
        return;
      }
      const placeId = values.place?.place_id;
      if (!placeId) return;
      if (prevPlaceId === placeId) return;
      prevPlaceId = placeId;

      try {
        const geocodingRes = (await geocode(placeId))[0];
        if (!geocodingRes) return;

        const { country, city, zipCode, address } = getAddressComponents(geocodingRes);

        form.change('country', country);
        form.change('address', address);
        form.change('city', city);
        form.change('zip', zipCode);
        form.change('geocode', geocodingRes);
      } catch (error) {
        let errorMessage = 'Failed to get address. Please try again';
        if (error instanceof Error) errorMessage = error.message;
        showErrorSnackbar(errorMessage);
      }
    },
    { values: true },
  );

  return () => unsubscribe();
};
